<template>
  <BaseModal
    :is-visible="isVisible"
    title="Edit Staff Profile"
    :subtitle="employee ? `Update details for ${employee.first_name} ${employee.last_name}` : 'Update staff information'"
    :icon="UserPen"
    size="3xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
    <form @submit.prevent="handleSubmit" id="editStaffProfileForm" class="space-y-6">
      <!-- Section 1: Personal Information -->
      <div class="p-5 rounded-2xl bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200/80 dark:border-gray-700/80 space-y-4">
        <div class="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
          <User class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
          <h3 class="text-sm font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200">
            Personal Information
          </h3>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- First Name -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              First Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.first_name"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              :class="errors.first_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'"
              placeholder="First name"
              required
            />
            <p v-if="errors.first_name" class="mt-1 text-xs text-red-500 font-medium">{{ errors.first_name[0] }}</p>
          </div>

          <!-- Middle Name -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Middle Name
            </label>
            <input
              v-model="formData.middle_name"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="Middle name (optional)"
            />
          </div>

          <!-- Last Name -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Last Name <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.last_name"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              :class="errors.last_name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'"
              placeholder="Last name"
              required
            />
            <p v-if="errors.last_name" class="mt-1 text-xs text-red-500 font-medium">{{ errors.last_name[0] }}</p>
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Date of Birth -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Date of Birth
            </label>
            <input
              v-model="formData.date_of_birth"
              type="date"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
            />
          </div>

          <!-- Gender -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Gender
            </label>
            <select
              v-model="formData.gender"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Non-Binary">Non-Binary</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
          </div>

          <!-- SSN (Last 4) -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              SSN (Last 4 digits)
            </label>
            <input
              v-model="formData.ssn_last_four"
              type="text"
              maxlength="4"
              pattern="\d{4}"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="e.g. 1234"
            />
          </div>
        </div>
      </div>

      <!-- Section 2: Contact & Address Information -->
      <div class="p-5 rounded-2xl bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200/80 dark:border-gray-700/80 space-y-4">
        <div class="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
          <MapPin class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
          <h3 class="text-sm font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200">
            Contact & Address Information
          </h3>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Email Address -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Email Address <span class="text-red-500">*</span>
            </label>
            <input
              v-model="formData.email"
              type="email"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              :class="errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'"
              placeholder="staff@jeducare.com"
              required
            />
            <p v-if="errors.email" class="mt-1 text-xs text-red-500 font-medium">{{ errors.email[0] }}</p>
          </div>

          <!-- Phone Number -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Phone Number
            </label>
            <input
              v-model="formData.phone_number"
              type="tel"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="(555) 000-0000"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-4 pt-1">
          <!-- Street Address -->
          <div class="sm:col-span-3 md:col-span-4">
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Street Address
            </label>
            <input
              v-model="formData.street_address"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="123 Main St, Apt 4B"
            />
          </div>

          <!-- City -->
          <div class="sm:col-span-2">
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              City
            </label>
            <input
              v-model="formData.city"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="City"
            />
          </div>

          <!-- State -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              State
            </label>
            <input
              v-model="formData.state"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="e.g. MD"
            />
          </div>

          <!-- Zip Code -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Zip Code
            </label>
            <input
              v-model="formData.zip_code"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="Zip Code"
            />
          </div>
        </div>
      </div>

      <!-- Section 3: Employment Details -->
      <div class="p-5 rounded-2xl bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200/80 dark:border-gray-700/80 space-y-4">
        <div class="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
          <Briefcase class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
          <h3 class="text-sm font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200">
            Employment Details
          </h3>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Position / Job Title -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Position / Job Title
            </label>
            <input
              v-model="formData.position"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="e.g. Direct Support Professional"
            />
          </div>

          <!-- Department -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Department
            </label>
            <input
              v-model="formData.department"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="e.g. Care Services"
            />
          </div>

          <!-- Hire Date -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Date of Hire
            </label>
            <input
              v-model="formData.hire_date"
              type="date"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
            />
          </div>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
          <!-- Work State Indicator -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Work State / Jurisdiction
            </label>
            <select
              v-model="formData.work_state"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
            >
              <option value="">Select Work State</option>
              <option value="Maryland">Maryland</option>
              <option value="DC">DC</option>
              <option value="Both">Both (Maryland & DC)</option>
            </select>
          </div>

          <!-- Client Group / Service Population -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Client Group / Service Population
            </label>
            <select
              v-model="formData.service_population"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
            >
              <option value="">Select Client Group</option>
              <option value="Adults">Adults</option>
              <option value="Kids">Kids</option>
              <option value="Both">Both (Adults & Kids)</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Section 4: Emergency Contact Information -->
      <div class="p-5 rounded-2xl bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200/80 dark:border-gray-700/80 space-y-4">
        <div class="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
          <AlertTriangle class="w-4 h-4 text-amber-500" />
          <h3 class="text-sm font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200">
            Emergency Contact
          </h3>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Contact Name -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Emergency Contact Name
            </label>
            <input
              v-model="formData.emergency_contact_name"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="Full name"
            />
          </div>

          <!-- Relationship -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Relationship
            </label>
            <input
              v-model="formData.emergency_contact_relation"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="e.g. Spouse, Parent, Sibling"
            />
          </div>

          <!-- Phone Number -->
          <div>
            <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Emergency Phone Number
            </label>
            <input
              v-model="formData.emergency_contact_phone"
              type="tel"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              placeholder="(555) 000-0000"
            />
          </div>
        </div>
      </div>

      <!-- Section 5: Password Reset (Optional) -->
      <div class="p-5 rounded-2xl bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200/80 dark:border-gray-700/80 space-y-4">
        <div class="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2">
          <div class="flex items-center gap-2">
            <Key class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <h3 class="text-sm font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200">
              Reset Password (Optional)
            </h3>
          </div>

          <label class="relative inline-flex items-center cursor-pointer">
            <input type="checkbox" v-model="shouldUpdatePassword" class="sr-only peer" />
            <div class="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-secondary"></div>
            <span class="ml-2 text-xs font-medium text-gray-600 dark:text-gray-300">Enable</span>
          </label>
        </div>

        <div v-if="shouldUpdatePassword" class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 items-start">
          <div>
            <div class="flex items-center justify-between h-5 mb-1.5">
              <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                New Password <span class="text-red-500">*</span>
              </label>
              <button
                type="button"
                @click="generatePassword"
                class="text-[11px] text-secondary dark:text-secondary-dark-text hover:underline font-semibold cursor-pointer"
              >
                Generate
              </button>
            </div>
            <input
              v-model="formData.password"
              type="password"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              :class="errors.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'"
              placeholder="Minimum 8 characters, 1 symbol"
              minlength="8"
              :required="shouldUpdatePassword"
            />
            <p v-if="errors.password" class="mt-1 text-xs text-red-500 font-medium">{{ errors.password[0] }}</p>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Password must be at least 8 characters long with at least one symbol
            </p>
          </div>

          <div>
            <div class="flex items-center justify-between h-5 mb-1.5">
              <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300">
                Confirm New Password <span class="text-red-500">*</span>
              </label>
            </div>
            <input
              v-model="formData.password_confirmation"
              type="text"
              class="w-full px-3.5 py-2.5 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition focus:ring-2 focus:ring-secondary focus:border-secondary outline-none shadow-sm"
              :class="errors.password_confirmation ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'"
              placeholder="Confirm password"
              :required="shouldUpdatePassword"
            />
            <p v-if="errors.password_confirmation" class="mt-1 text-xs text-red-500 font-medium">
              {{ errors.password_confirmation[0] }}
            </p>
          </div>
        </div>
      </div>
    </form>

    <template #footer>
      <div class="flex items-center justify-end gap-3 w-full">
        <BaseButton
          text="Cancel"
          variant="outline"
          size="md"
          :disabled="isSubmitting"
          @click="closeModal"
        />
        <BaseButton
          text="Save Changes"
          variant="primary"
          size="md"
          :loading="isSubmitting"
          :icon="Save"
          @click="handleSubmit"
        />
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, reactive } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { employeeService } from '@/services/user'
import { useToastService } from '@/services/toast'
import type { employee } from '@/types/employee'
import {
  UserPen,
  User,
  MapPin,
  Briefcase,
  AlertTriangle,
  Key,
  Save,
} from 'lucide-vue-next'

const props = defineProps<{
  isVisible: boolean
  employee: employee | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'success', updatedEmployee: employee): void
}>()

const toast = useToastService()
const isSubmitting = ref(false)
const shouldUpdatePassword = ref(false)
const errors = ref<Record<string, string[]>>({})

const formData = reactive({
  first_name: '',
  middle_name: '',
  last_name: '',
  date_of_birth: '',
  gender: '',
  ssn_last_four: '',
  email: '',
  phone_number: '',
  street_address: '',
  city: '',
  state: '',
  zip_code: '',
  position: '',
  department: '',
  work_state: '',
  service_population: '',
  hire_date: '',
  emergency_contact_name: '',
  emergency_contact_relation: '',
  emergency_contact_phone: '',
  password: '',
  password_confirmation: '',
})

// Initialize or reset form data from prop
watch(
  [() => props.employee, () => props.isVisible],
  ([newEmployee, isVisible]) => {
    if (newEmployee && isVisible) {
      formData.first_name = newEmployee.first_name || ''
      formData.middle_name = newEmployee.middle_name || ''
      formData.last_name = newEmployee.last_name || ''
      formData.date_of_birth = newEmployee.date_of_birth || ''
      formData.gender = newEmployee.gender || ''
      formData.ssn_last_four = newEmployee.ssn_last_four || ''
      formData.email = newEmployee.email || ''
      formData.phone_number = newEmployee.phone_number || ''
      formData.street_address = newEmployee.street_address || ''
      formData.city = newEmployee.city || ''
      formData.state = newEmployee.state || ''
      formData.zip_code = newEmployee.zip_code || ''
      formData.position = newEmployee.position || ''
      formData.department = newEmployee.department || ''
      formData.work_state = newEmployee.work_state || ''
      formData.service_population = newEmployee.service_population || ''
      formData.hire_date = newEmployee.hire_date || ''
      formData.emergency_contact_name = newEmployee.emergency_contact_name || ''
      formData.emergency_contact_relation = newEmployee.emergency_contact_relation || ''
      formData.emergency_contact_phone = newEmployee.emergency_contact_phone || ''
      formData.password = ''
      formData.password_confirmation = ''
      shouldUpdatePassword.value = false
      errors.value = {}
    }
  },
  { immediate: true }
)

const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$'
  let pwd = ''
  for (let i = 0; i < 12; i++) {
    pwd += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  formData.password = pwd
  formData.password_confirmation = pwd
  toast.info('Generated password copied into fields')
}

const handleSubmit = async () => {
  if (!props.employee) return

  errors.value = {}

  if (shouldUpdatePassword.value) {
    if (!formData.password) {
      errors.value.password = ['New password is required when reset password is enabled.']
      return
    }

    if (!formData.password_confirmation) {
      errors.value.password_confirmation = ['Password confirmation is required.']
      return
    }

    const hasPasswordSymbol = /[!@#$%^&*(),.?":{}|<>_~\-+=]/.test(formData.password)

    if (formData.password.length < 8 || !hasPasswordSymbol) {
      errors.value.password = ['Password must be at least 8 characters long with at least one symbol.']
      return
    }

    if (formData.password !== formData.password_confirmation) {
      errors.value.password_confirmation = ['Password confirmation does not match.']
      return
    }
  }

  isSubmitting.value = true

  try {
    const payload: any = {
      first_name: formData.first_name,
      middle_name: formData.middle_name || null,
      last_name: formData.last_name,
      date_of_birth: formData.date_of_birth || null,
      gender: formData.gender || null,
      ssn_last_four: formData.ssn_last_four || null,
      email: formData.email,
      phone_number: formData.phone_number || null,
      street_address: formData.street_address || null,
      city: formData.city || null,
      state: formData.state || null,
      zip_code: formData.zip_code || null,
      position: formData.position || null,
      department: formData.department || null,
      work_state: formData.work_state || null,
      service_population: formData.service_population || null,
      hire_date: formData.hire_date || null,
      emergency_contact_name: formData.emergency_contact_name || null,
      emergency_contact_relation: formData.emergency_contact_relation || null,
      emergency_contact_phone: formData.emergency_contact_phone || null,
    }

    if (shouldUpdatePassword.value && formData.password) {
      payload.password = formData.password
      payload.password_confirmation = formData.password_confirmation
    }

    const updated = await employeeService.updateEmployee(props.employee.user_details_id, payload)
    toast.success('Staff profile updated successfully')
    emit('success', updated)
    emit('close')
  } catch (err: any) {
    if (err?.response?.status === 422 && err.response.data?.errors) {
      errors.value = err.response.data.errors
      toast.error(err.response.data.message || 'Please check form errors.')
    } else {
      toast.error('Failed to update staff profile. Please try again.')
    }
  } finally {
    isSubmitting.value = false
  }
}

const closeModal = () => {
  if (!isSubmitting.value) {
    emit('close')
  }
}
</script>

